import { definedMap, assert } from "@glideapps/ts-necessities";

import type { SerializedComponent } from "../../components/SerializedApp";
import { logError } from "../../debug-print";
import { getDocURL } from "../../docUrl";
import { getFeatureSetting } from "../../feature-settings";
import { defaultActionForColumn, makeScreenContextForActions } from "../actions";
import { ListItemFlags } from "../array-screens/list-item-flags";
import { handleActionIcon, iconForActions, makeText, withCaption } from "../builder-utils";
import type {
    ComponentKind,
    InputOutputTables,
    LegacyPropertyDescription,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
    ActionComponentDescription,
    Description,
    ActionDescription,
} from "../description";
import {
    ActionKind,
    getTableColumn,
    getTableColumnDisplayName,
    hasInputContext,
    isPrimitiveType,
    PropertyKind,
    makeEmptyComponentDescription,
} from "../description";
import { makeCaptionStringPropertyDescriptor, makeTextPropertyDescriptor } from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import { SwitchPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import {
    getColumnProperty,
    getIconProperty,
    makeActionProperty,
    makeColumnProperty,
    makeStringProperty,
    makeSwitchProperty,
} from "./property-utils";
import type {
    ActionPropertyDescriptor,
    ComponentBuilder,
    AppDescriptionContext,
    ComponentDescriptor,
    SchemaInspector,
} from ".";
import { tokenForProperty } from "../actions/base";

const ComponentKindPrimitive: ComponentKind = "primitive";

const componentName = "Action Text";

interface PrimitiveComponentDescription extends ActionComponentDescription {
    readonly propertyName: LegacyPropertyDescription;
    readonly caption: LegacyPropertyDescription | undefined;
    readonly truncate: PropertyDescription | undefined;
    readonly actionIcon: PropertyDescription | undefined;
}

const truncatePropertyHandler = new SwitchPropertyHandler(
    {
        truncate: false,
    },
    "Trim text to first 4 lines",
    PropertySection.Options
);

const primitiveActionKinds: readonly ActionKind[] = [
    // NOTE: The first item here is the "default" action, so don't move it.
    ActionKind.PushDetailScreen,
    ActionKind.PushEditScreen,
    ActionKind.FormScreen,
    ActionKind.PushFreeScreen,
    ActionKind.CopyToClipboardWithArgs,
    ActionKind.OpenLinkWithArgs,
    ActionKind.OpenMapWithArgs,
    ActionKind.PhoneCallWithArgs,
    ActionKind.TextMessageWithArgs,
    ActionKind.ShowShareSheetWithArgs,
    ActionKind.SendEmailWithArgs,
    ActionKind.AddRow,
    ActionKind.SetColumns,
    ActionKind.DeleteRow,
    ActionKind.Zapier,
    ActionKind.Reshuffle,
    ActionKind.Increment,
    ActionKind.SignIn,
    ActionKind.PopScreen,
    ActionKind.CloseModal,
    ActionKind.NavigateToTab,
    ActionKind.ShowToast,
    ActionKind.DeliverEmail,
    ActionKind.ScanCode,
    ActionKind.LiveScanLinearBarcode,
    ActionKind.PlaySound,
    ActionKind.Webhook,
    // legacy - these have to be in here, or we refuse to build them
    ActionKind.OpenLink,
    ActionKind.PhoneCall,
    ActionKind.TextMessage,
    ActionKind.CopyToClipboard,
    ActionKind.OpenMap,
    ActionKind.SendEmail,
    ActionKind.ShowShareSheet,
    ActionKind.OpenWebView,
];

const primitiveActionKindsWithDebug: ActionKind[] = ([] as ActionKind[]).concat(primitiveActionKinds, [
    ActionKind.Alert,
]);

export function getPrimitiveActionKinds(
    screenHasInputContext: boolean,
    appHasUserProfiles: boolean
): readonly ActionKind[] {
    let kinds = getFeatureSetting("debugActions") ? primitiveActionKindsWithDebug : primitiveActionKinds;
    if (!screenHasInputContext) {
        kinds = kinds.filter(
            // FIXME: Filter it out here, too
            k => k !== ActionKind.AddRow && k !== ActionKind.DeleteRow && k !== ActionKind.PushDetailScreen
        );
    }
    if (appHasUserProfiles) {
        kinds = [...kinds, ActionKind.PushUserProfileScreen];
    }
    return kinds;
}

export function getDefaultPrimitiveActionKinds(
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined
): readonly ActionKind[] {
    return getPrimitiveActionKinds(
        hasInputContext(mutatingScreenKind),
        schema === undefined || schema.userProfileTableInfo !== undefined
    );
}

export function makeDefaultActionDescriptorWithKinds<T extends Description>(
    kinds: readonly ActionKind[],
    defaultAction: boolean | ((table: TableGlideType, desc: T) => ActionDescription | undefined)
): ActionPropertyDescriptor<T> {
    return {
        property: { name: "actions" },
        kind: PropertyKind.Action,
        label: "Action",
        kinds,
        defaultAction,
        section: PropertySection.Action,
    };
}

export function makeDefaultActionDescriptor<T extends Description>(
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    defaultAction: boolean | ((table: TableGlideType, desc: T) => ActionDescription | undefined),
    extraKinds?: readonly ActionKind[]
): ActionPropertyDescriptor<T> {
    const kinds = [...(extraKinds ?? []), ...getDefaultPrimitiveActionKinds(schema, mutatingScreenKind)];
    return makeDefaultActionDescriptorWithKinds(kinds, defaultAction);
}

export class PrimitiveComponentHandler extends ComponentHandlerBase<PrimitiveComponentDescription> {
    constructor() {
        super(ComponentKindPrimitive);
    }

    public get needsColumns(): boolean {
        return false;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: PrimitiveComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const text = makeText(builder, desc.propertyName, tables.input, true, true, true);
        if (text === undefined) return b.emptyUI;

        const { actions } = this.getActions(desc, tables.input, builder, mutatingScreenKind);
        const { screenContext, lets } = makeScreenContextForActions(builder, actions, tables, mutatingScreenKind);

        lets.push(["text", text]);

        const onTap = builder.makeSequenceFromActions(
            actions,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );

        const actionIcon = getIconProperty(desc.actionIcon);
        const iconName = actionIcon ?? definedMap(actions, iconForActions);
        const truncate = truncatePropertyHandler.getSwitch(desc);

        let icon: SerializedComponent | undefined;
        let flags = ListItemFlags.WrapText | ListItemFlags.InvertTitleAndSubtitle | ListItemFlags.ConvertNewlines;
        if (truncate) {
            flags |= ListItemFlags.TruncateWrap;
        }
        if (iconName === undefined) {
            flags |= ListItemFlags.DisableChevron;
        } else if (typeof iconName === "string") {
            icon = b.constant(iconName);
        }

        if (actions.length === 0) {
            flags |= ListItemFlags.LightWeight;
        }

        return withCaption(builder, tables.input, desc.caption, lets, caption => {
            return handleActionIcon(b, onTap, icon, false, (ot, ic) =>
                b.ifIsNotEmpty(
                    b.getVariable("text"),
                    b.adaptiveListItem(
                        b.getVariable("text"),
                        caption,
                        undefined,
                        undefined,
                        ic,
                        undefined,
                        ot,
                        undefined,
                        undefined,
                        b.constant(flags),
                        undefined,
                        undefined,
                        undefined
                    )
                )
            );
        });
    }

    public getDescriptor(
        desc: PrimitiveComponentDescription | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<PrimitiveComponentDescription> {
        const actions = definedMap(desc, d => this.getActions(d, table, ccc, mutatingScreenKind))?.actions;
        let icon = definedMap(actions, iconForActions);
        if (icon === true) icon = undefined;
        return {
            name: componentName,
            description: "A labeled value",
            img: "componentActionText",
            group: "Text",
            helpUrl: getDocURL("actionText"),
            properties: [
                makeTextPropertyDescriptor("propertyName", "Text", "Enter text", true, mutatingScreenKind, {
                    isMultiLine: true,
                    preferredNames: ["text"],
                    columnFirst: true,
                    isDefaultCaption: true,
                }),
                makeCaptionStringPropertyDescriptor("Data", false, mutatingScreenKind),
                truncatePropertyHandler,
                {
                    kind: PropertyKind.Icon,
                    property: { name: "actionIcon" },
                    label: "Icon",
                    defaultIcon: icon,
                    section: PropertySection.Design,
                },
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }

    protected getActionDescriptors(
        _desc: PrimitiveComponentDescription | undefined,
        _table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor<PrimitiveComponentDescription>[] {
        return [
            makeDefaultActionDescriptor(schema, mutatingScreenKind, (t, d: PrimitiveComponentDescription) => {
                const columnProperty = getColumnProperty(d.propertyName);
                if (columnProperty === undefined) return undefined;

                const column = getTableColumn(t, columnProperty);
                if (column === undefined) {
                    logError("Could not find column for table for primitive action", columnProperty, t);
                    return undefined;
                }
                return defaultActionForColumn(column);
            }),
        ];
    }

    public static defaultComponent(column: TableColumn): PrimitiveComponentDescription {
        assert(isPrimitiveType(column.type));

        const action = defaultActionForColumn(column);
        return {
            ...makeEmptyComponentDescription(ComponentKindPrimitive),
            propertyName: makeColumnProperty(column.name),
            caption: makeStringProperty(getTableColumnDisplayName(column)),
            actions: definedMap(action, makeActionProperty),
            truncate: makeSwitchProperty(false),
            actionIcon: undefined,
        };
    }

    public getDescriptiveName(
        desc: PrimitiveComponentDescription,
        tables: InputOutputTables,
        ccc: AppDescriptionContext
    ): [string, string] {
        return [componentName, tokenForProperty(desc.propertyName, tables, ccc)?.value ?? ""];
    }
}
